import React, { useState, useEffect } from 'react';
import { Search, Bell, Settings, MapPin, Zap, Navigation, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface NotificationPref {
    id: number;
    user_id: number;
    user_username: string;
    user_name?: string;
    user_avatar?: string;
    arrival_enabled: boolean;
    departure_enabled: boolean;
    low_battery_enabled: boolean;
    speed_alert_enabled: boolean;
    route_deviation_enabled: boolean;
    trip_completed_enabled: boolean;
    poi_enabled: boolean;
    poi_throttle_minutes: number;
    battery_threshold: number;
    speed_threshold: number;
    updated_at: string;
}

interface PrefStats {
    total_users: number;
    arrival_enabled: number;
    speed_alert_enabled: number;
    poi_enabled: number;
}

const NotificationPrefManagement: React.FC = () => {
    const [prefs, setPrefs] = useState<NotificationPref[]>([]);
    const [stats, setStats] = useState<PrefStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [prefsRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/notification-preferences'),
                axios.get('https://api.lifeshare.app/api/v1/admin/notification-preferences/stats')
            ]);
            setPrefs(prefsRes.data.preferences || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const BoolBadge = ({ value }: { value: boolean }) => (
        <span className={`w-3 h-3 rounded-full ${value ? 'bg-green-500' : 'bg-gray-300'}`} />
    );

    const filteredPrefs = prefs.filter(p =>
        (p.user_username || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (p.user_name || '').toLowerCase().includes(searchText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Bildirim Tercihleri</h1>
                        <p className="text-sm text-gray-500 mt-1">Kullanıcı bildirim ayarlarını görüntüle</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Settings className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.total_users}</p>
                            <p className="text-xs text-purple-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><MapPin className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.arrival_enabled}</p>
                            <p className="text-xs text-green-600">Varış Açık</p>
                        </div>
                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Zap className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-yellow-700">{stats.speed_alert_enabled}</p>
                            <p className="text-xs text-yellow-600">Hız Uyarısı</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Navigation className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-blue-700">{stats.poi_enabled}</p>
                            <p className="text-xs text-blue-600">POI Açık</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* List */}
                {filteredPrefs.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Settings className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Bildirim tercihi bulunamadı</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredPrefs.map((pref) => (
                            <div key={pref.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-start gap-3 mb-3">
                                    {pref.user_avatar ? (
                                        <img src={`https://api.lifeshare.app${pref.user_avatar}`} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                                            <Bell className="w-5 h-5" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 text-sm truncate">{pref.user_name || pref.user_username}</p>
                                        <p className="text-xs text-gray-500">@{pref.user_username}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.arrival_enabled} /><span>Varış</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.departure_enabled} /><span>Ayrılış</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.low_battery_enabled} /><span>Düşük Pil</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.speed_alert_enabled} /><span>Hız Uyarısı</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.route_deviation_enabled} /><span>Rota Sapması</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl"><BoolBadge value={pref.trip_completed_enabled} /><span>Yolculuk Bitti</span></div>
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl col-span-2"><BoolBadge value={pref.poi_enabled} /><span>POI Bildirimi</span></div>
                                </div>

                                <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-3 gap-2 text-xs text-gray-500">
                                    <span>🔋 Pil Eşik: {pref.battery_threshold}%</span>
                                    <span>⚡ Hız Limiti: {pref.speed_threshold} km/s</span>
                                    <span>⏱️ POI Aralık: {pref.poi_throttle_minutes} dk</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationPrefManagement;
